import { PET_MOVE, Pt, SpeedProfile, cruiseSpeed, gaitFor, roundPath, GAIT_FALLBACK, GaitName, Gaits, clipRate, yawGap } from "./petGait";

/** A move shorter than this (cells) is a shuffle onto the spot, not a move. */
const SETTLE = 0.04;

/**
 * A move from one spot to another along waypoints (the cells a troop would walk: round towers and the fortress, never
 * over a cliff or a hole), with the corners of that cell path rounded off: a trot for a short shuffle, a gallop for
 * anything longer, at a speed that ramps up, cruises and ramps down to land on the spot (petGait.ts SpeedProfile). A
 * move that sets off well away from where the pet faces turns on the spot first. It reports the speed, the gait and
 * the clip rate that keeps the paws planted, and the heading of the way under it; the owner turns the body toward
 * that heading at a capped rate. Pure.
 */
export class PetHop {
  private path: Pt[] = [];
  /** Way covered at each point of `path`. */
  private at: number[] = [];
  private profile: SpeedProfile | null = null;
  /** Seconds into the move (negative: still turning on the spot), and the move's gait. */
  private t = 0;
  private gaitNow: GaitName | null = null;
  private tx = 0; private tz = 0;
  /** Where it is now (world x / z), its speed (world units a second), and the heading of the way it is on (yaw). */
  x = 0; z = 0; speed = 0; heading = 0;

  /** `gaits`: the model's measured gaits; `scale`: how large it is drawn (its strides scale with it). */
  constructor(private gaits: Gaits = GAIT_FALLBACK, private scale = 1) {}

  /** A body in another coat or size: the next move uses its gaits. */
  configure(gaits: Gaits, scale: number): void { this.gaits = gaits; this.scale = scale; }

  /** Stands at (x, z) at once (the first frame, a resumed battle). */
  place(x: number, z: number): void { this.tx = this.x = x; this.tz = this.z = z; this.profile = null; this.gaitNow = null; this.speed = 0; }

  /**
   * Heads for (x, z) from where it is now, through `via` (world x / z, in order); `yaw` is the way the body faces now.
   * A spot within SETTLE is just taken. A new move mid-move starts from where it is, keeping the gait it is in.
   */
  go(x: number, z: number, via: readonly Pt[] = [], yaw = this.heading): void {
    this.tx = x; this.tz = z;
    if (Math.hypot(x - this.x, z - this.z) < SETTLE) { if (!this.moving) { this.x = x; this.z = z; } return; }
    const pts: Pt[] = [];
    for (const p of [{ x: this.x, z: this.z }, ...via, { x, z }]) {
      const last = pts[pts.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.z - last.z) >= SETTLE) pts.push(p);
    }
    this.path = roundPath(pts);
    this.at = [0];
    for (let i = 1; i < this.path.length; i++) this.at.push(this.at[i - 1] + Math.hypot(this.path[i].x - this.path[i - 1].x, this.path[i].z - this.path[i - 1].z));
    const length = this.at[this.at.length - 1];
    const was = this.gaitNow;
    const gait = was === "run" ? "run" : gaitFor(length);
    this.gaitNow = gait;
    this.profile = new SpeedProfile(length, cruiseSpeed(gait, this.gaits, this.scale), PET_MOVE.accel[gait], this.speed);
    this.heading = this.headingAt(0);
    // from a stand and facing well away from the way: turn on the spot first (no crab-walk), at the body's turn rate
    const off = yawGap(yaw, this.heading);
    this.t = was === null && off > PET_MOVE.turnFirst ? -(off - PET_MOVE.turnFirst) / PET_MOVE.turnRate : 0;
  }

  get moving(): boolean { return this.profile !== null; }
  /** The move's gait while it moves (also while it turns on the spot to set off), else null. */
  get gait(): GaitName | null { return this.gaitNow; }
  /** Where it is going (world x / z). */
  get target(): Pt { return { x: this.tx, z: this.tz }; }
  /** Seconds of the move left (0 at a stand), and whether it has set off (not still turning on the spot). */
  get left(): number { return this.profile ? Math.max(0, this.profile.duration - this.t) : 0; }
  get setOff(): boolean { return this.profile !== null && this.t >= 0; }
  /** The locomotion clip's rate that keeps its paws planted at the speed it moves now (1 at a stand). */
  get rate(): number { return this.gaitNow && this.speed > 0 ? clipRate(this.speed, this.gaits[this.gaitNow], this.scale) : 1; }

  /** Advances `dt` seconds. */
  step(dt: number): void {
    const p = this.profile;
    if (!p) { this.speed = 0; return; }
    this.t += dt;
    if (this.t >= p.duration) { this.x = this.tx; this.z = this.tz; this.speed = 0; this.profile = null; this.gaitNow = null; return; }
    this.speed = p.speedAt(this.t);
    this.sample(p.distanceAt(this.t));
  }

  /** The point `d` along the way, and the heading of the stretch it is on. */
  private sample(d: number): void {
    const k = this.segment(d);
    const a = this.path[k - 1], b = this.path[k], span = this.at[k] - this.at[k - 1];
    const u = span > 0 ? Math.min(1, Math.max(0, (d - this.at[k - 1]) / span)) : 1;
    this.x = a.x + (b.x - a.x) * u; this.z = a.z + (b.z - a.z) * u;
    if (span > 0) this.heading = Math.atan2(b.x - a.x, b.z - a.z);
  }

  private headingAt(d: number): number {
    const k = this.segment(d), a = this.path[k - 1], b = this.path[k];
    return Math.atan2(b.x - a.x, b.z - a.z);
  }

  /** The index of the point that ends the stretch `d` is on (1 … last). */
  private segment(d: number): number {
    let k = 1;
    while (k < this.path.length - 1 && this.at[k] < d) k++;
    return k;
  }
}
